import React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Input } from '../ui/Input';
import type { CreateInvitationData } from '../../types/team';

interface InvitationFormProps {
  onSubmit: (data: CreateInvitationData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * Form for sending team invitations
 */
export const InvitationForm: React.FC<InvitationFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateInvitationData>();

  const handleFormSubmit = (data: CreateInvitationData) => {
    // Always send as manager role for now
    onSubmit({ ...data, role: 'manager' });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormField
        label="Email Address"
        error={errors.email?.message}
        required
        htmlFor="email"
      >
        <Input
          id="email"
          type="email"
          placeholder="colleague@example.com"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
          error={!!errors.email}
          disabled={isSubmitting}
        />
      </FormField>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> The recipient will receive an email with an invitation link.
          The invitation will expire in 7 days.
        </p>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting}>
          Send Invitation
        </Button>
      </div>
    </form>
  );
};

export default InvitationForm;
